import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { from, isObservable, switchMap } from 'rxjs';
import { NMI_CONFIG, NmiConfig } from './nmi.config';

async function resolveToken(config: NmiConfig): Promise<string> {
  if (!config.tokenFn) return '';
  const raw = config.tokenFn();
  if (isObservable(raw)) {
    return new Promise<string>((resolve, reject) =>
      raw.subscribe({ next: resolve, error: reject })
    );
  }
  return raw;
}

function resolveBranchId(config: NmiConfig): string | null {
  if (!config.branchId) return null;
  return typeof config.branchId === 'function'
    ? config.branchId()
    : config.branchId;
}

/**
 * Closure-based interceptor factory — preferred for standalone apps to avoid
 * inject() context issues when library and app share separate Angular instances.
 *
 * Usage (app.config.ts):
 *   const nmiConfig = { apiUrl: '...', apiKey: '...' };
 *   provideHttpClient(withInterceptors([createNmiInterceptorFn(nmiConfig)]))
 *   ...provideNmi(nmiConfig)
 */
export function createNmiInterceptorFn(config: NmiConfig): HttpInterceptorFn {
  return (req, next) => {
    if (!req.url.startsWith(config.apiUrl)) return next(req);

    if (config.apiKey) {
      return next(req.clone({ setHeaders: { 'X-Api-Key': config.apiKey } }));
    }

    return from(resolveToken(config)).pipe(
      switchMap(token => {
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const branchId = resolveBranchId(config);
        if (branchId) headers['X-Branch-Id'] = branchId;
        return next(req.clone({ setHeaders: headers }));
      })
    );
  };
}

/**
 * DI-based functional interceptor — use when library and app share the SAME
 * Angular instance (e.g. library built/published as npm package).
 *
 * Usage (app.config.ts):
 *   provideHttpClient(withInterceptors([nmiAuthInterceptorFn]))
 */
export const nmiAuthInterceptorFn: HttpInterceptorFn = (req, next) => {
  const config = inject(NMI_CONFIG);

  if (!req.url.startsWith(config.apiUrl)) {
    return next(req);
  }

  if (config.apiKey) {
    return next(req.clone({ setHeaders: { 'X-Api-Key': config.apiKey } }));
  }

  return from(resolveToken(config)).pipe(
    switchMap(token => {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const branchId = resolveBranchId(config);
      if (branchId) headers['X-Branch-Id'] = branchId;
      return next(req.clone({ setHeaders: headers }));
    })
  );
};
